/**
 * Shared Interactive Service Area Map section: Heatmap, Coverage, and Route maps.
 * Used on Fleet Overview and Control Centre > Telematics.
 *
 * Primary source: ZeeCore truth-layer GPS (vehicle_position_observed).
 * Fallback: legacy batch geographic JSON on the analytics server.
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { usePeriod } from '../contexts/PeriodContext';
import { useUseCase } from '../contexts/UseCaseContext';
import { useDemoMode } from '../contexts/DemoModeContext';
import { GeographicHeatmap } from './maps/GeographicHeatmap';
import { GeographicCoverageMap } from './maps/GeographicCoverageMap';
import { GeographicRouteMap } from './maps/GeographicRouteMap';
import { Map as MapView, MapControls, MapMarker, MarkerContent, MarkerTooltip } from '@/components/ui/map';
import { sanitizeMapPopupHtml } from '../utils/sanitizeHtml';
import { API_ENDPOINTS } from '../config/api';
import { STATUS_COLORS } from '../config/colors';
import { authenticatedFetch } from '../utils/authenticatedFetch';

/** Derive list of YYYY-MM-DD dates from period string (e.g. 20260101_to_20260228). */
function getDatesFromPeriod(period: string | null | undefined): string[] {
  if (!period || typeof period !== 'string') return [];
  const p = period.trim();
  const match = p.match(/^(\d{8})_to_(\d{8})/) || p.match(/^(\d{8})\s+to\s+(\d{8})/i);
  let startY = 0, startM = 0, startD = 0, endY = 0, endM = 0, endD = 0;
  if (match) {
    const start = match[1], end = match[2];
    startY = parseInt(start.slice(0, 4), 10);
    startM = parseInt(start.slice(4, 6), 10);
    startD = parseInt(start.slice(6, 8), 10);
    endY = parseInt(end.slice(0, 4), 10);
    endM = parseInt(end.slice(4, 6), 10);
    endD = parseInt(end.slice(6, 8), 10);
  } else if (/^\d{8}$/.test(p)) {
    startY = endY = parseInt(p.slice(0, 4), 10);
    startM = endM = parseInt(p.slice(4, 6), 10);
    startD = endD = parseInt(p.slice(6, 8), 10);
  } else {
    return [];
  }
  const out: string[] = [];
  const startDate = new Date(startY, startM - 1, startD);
  const endDate = new Date(endY, endM - 1, endD);
  const cur = new Date(startDate);
  while (cur <= endDate) {
    out.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

function periodContainsMonth(period: string | null | undefined, month: { year: number; month: number }): boolean {
  if (!period || typeof period !== 'string') return false;
  const m = month?.month ?? 0;
  const y = month?.year ?? 0;
  if (!y || m < 1 || m > 12) return false;
  const p = period.trim();
  const match = p.match(/^(\d{8})_to_(\d{8})/) || p.match(/^(\d{8})\s+to\s+(\d{8})/i);
  if (!match) return false;
  const startYm = match[1].slice(0, 6);
  const endYm = match[2].slice(0, 6);
  const targetYm = `${y}${String(m).padStart(2, '0')}`;
  return targetYm >= startYm && targetYm <= endYm;
}

/** Resolve ISO from/to for truth-layer geographic queries. */
function resolveGeographicTimeRange(
  selectedMonth: { year: number; month: number } | null | undefined,
  selectedPeriod: string | null | undefined,
): { from: string; to: string } | null {
  if (selectedMonth && selectedMonth.month >= 1 && selectedMonth.month <= 12) {
    const y = selectedMonth.year;
    const m = selectedMonth.month;
    const lastDay = new Date(y, m, 0).getDate();
    const mm = String(m).padStart(2, '0');
    return {
      from: `${y}-${mm}-01T00:00:00.000Z`,
      to: `${y}-${mm}-${String(lastDay).padStart(2, '0')}T23:59:59.999Z`,
    };
  }
  const dates = getDatesFromPeriod(selectedPeriod);
  if (dates.length === 0) return null;
  return {
    from: `${dates[0]}T00:00:00.000Z`,
    to: `${dates[dates.length - 1]}T23:59:59.999Z`,
  };
}

function deepMergeConcatArrays(a: any, b: any): any {
  if (a == null) return b;
  if (b == null) return a;
  if (Array.isArray(a) && Array.isArray(b)) return [...a, ...b];
  if (Array.isArray(a) || Array.isArray(b)) return b;
  if (typeof a !== 'object' || typeof b !== 'object') return b;
  const out: any = { ...a };
  for (const key of Object.keys(b)) {
    out[key] = key in out ? deepMergeConcatArrays(out[key], b[key]) : b[key];
  }
  return out;
}

/** Collect YYYY-MM-DD keys that have at least one non-empty GPS/route entry from heatmap or route group_data. */
function getDatesWithGpsData(heatmapData: any, routeData: any): string[] {
  const dateSet = new Set<string>();
  const dateRe = /^\d{4}-\d{2}-\d{2}$/;

  function scanHeatmapGroupData(gd: any): void {
    if (!gd || typeof gd !== 'object') return;
    const scanEntry = (entry: any): void => {
      if (!entry || typeof entry !== 'object') return;
      if (Array.isArray(entry)) return;
      for (const key of Object.keys(entry)) {
        if (dateRe.test(key)) {
          const val = entry[key];
          const hasPoints = Array.isArray(val) && val.length > 0;
          const hasSegments = val && typeof val === 'object' && Array.isArray(val.segments) && val.segments.length > 0;
          if (hasPoints || hasSegments) dateSet.add(key);
        }
      }
    };
    if (gd.Vehicle || gd.vehicle) {
      const v = gd.Vehicle ?? gd.vehicle;
      Object.values(v).forEach(scanEntry);
    }
    if (gd.Type || gd.type) {
      const t = gd.Type ?? gd.type;
      Object.values(t).forEach(scanEntry);
    }
    if (gd.Station || gd.station) {
      const s = gd.Station ?? gd.station;
      Object.values(s).forEach(scanEntry);
    }
    if (gd.incident && typeof gd.incident === 'object') {
      scanHeatmapGroupData(gd.incident);
    }
    if (gd['non-operational'] && typeof gd['non-operational'] === 'object') {
      scanHeatmapGroupData(gd['non-operational']);
    }
  }

  if (heatmapData?.group_data) scanHeatmapGroupData(heatmapData.group_data);
  if (routeData?.group_data) scanHeatmapGroupData(routeData.group_data);

  return Array.from(dateSet).sort();
}

function hasGeographicVehicles(payload: any): boolean {
  const vehicles = payload?.group_data?.Vehicle;
  return Boolean(vehicles && typeof vehicles === 'object' && Object.keys(vehicles).length > 0);
}

interface InteractiveServiceAreaMapSectionProps {
  /** When provided, Geographic Insights use this to show Vehicle Reg (customerVehicleName) instead of raw ID in Group By "Vehicle Reg" value dropdown */
  getVehicleDisplayName?: (vehicleId: string) => string;
  /** When provided, heatmap/coverage/route layers show only data for this month (date keys YYYY-MM-DD in JSON). */
  selectedMonth?: { year: number; month: number } | null;
}

export function InteractiveServiceAreaMapSection({ getVehicleDisplayName, selectedMonth = null }: InteractiveServiceAreaMapSectionProps = {}) {
  const { selectedPeriod, periods } = usePeriod();
  const { useCase } = useUseCase();
  const { isDemoMode } = useDemoMode();
  const serviceType = useCase === 'emergency' ? 'emergency_services' : 'logistics';

  const [serviceAreaMapTab, setServiceAreaMapTab] = useState<'heatmap' | 'coverage' | 'route'>('heatmap');
  const [heatmapData, setHeatmapData] = useState<any>(null);
  const [heatmapDataLoading, setHeatmapDataLoading] = useState(false);
  const [heatmapDataError, setHeatmapDataError] = useState<string | null>(null);
  const [routeData, setRouteData] = useState<any>(null);
  const [routeDataLoading, setRouteDataLoading] = useState(false);
  const [routeDataError, setRouteDataError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  /** Only fetch GPS after the map section is near the viewport (avoids OOM on Fleet Report open). */
  const [mapInView, setMapInView] = useState(false);
  const mapSectionRef = useRef<HTMLDivElement>(null);
  const didAutoSelectDateRef = useRef(false);
  const heatmapFetchedKeyRef = useRef<string | null>(null);
  const routeFetchedKeyRef = useRef<string | null>(null);

  // Shared map selection state so Heatmap, Coverage, and Route stay in sync when switching tabs
  const [mapGroupBy, setMapGroupBy] = useState<'Vehicle' | 'Type' | 'Station'>('Vehicle');
  const [mapSelectedGroupValue, setMapSelectedGroupValue] = useState<string | null>(null);
  const [mapShowAll, setMapShowAll] = useState(false);
  const [mapSelectedActivity, setMapSelectedActivity] = useState<string>('incident');

  useEffect(() => {
    const el = mapSectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setMapInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setMapInView(true);
          observer.disconnect();
        }
      },
      { root: null, rootMargin: '200px 0px', threshold: 0.05 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const availableDates = useMemo(() => {
    // Only offer days that actually have GPS/route points ingested — never every calendar day.
    const withGps = getDatesWithGpsData(heatmapData, routeData);
    if (selectedMonth && selectedMonth.month >= 1 && selectedMonth.month <= 12) {
      const prefix = `${selectedMonth.year}-${String(selectedMonth.month).padStart(2, '0')}-`;
      return withGps.filter((d) => d.startsWith(prefix));
    }
    return withGps;
  }, [heatmapData, routeData, selectedMonth]);
  const [selectedMapDate, setSelectedMapDate] = useState<string | null>(null);

  const periodFetchKey = `${selectedMonth?.year ?? ''}-${selectedMonth?.month ?? ''}-${selectedPeriod ?? ''}-${serviceType}-${retryCount}`;
  const needsHeatmap = mapInView && (serviceAreaMapTab === 'heatmap' || serviceAreaMapTab === 'coverage');
  const needsRoute = mapInView && serviceAreaMapTab === 'route';

  useEffect(() => {
    didAutoSelectDateRef.current = false;
    setSelectedMapDate(null);
    heatmapFetchedKeyRef.current = null;
    routeFetchedKeyRef.current = null;
    setHeatmapData(null);
    setRouteData(null);
    setHeatmapDataError(null);
    setRouteDataError(null);
  }, [selectedMonth?.year, selectedMonth?.month, selectedPeriod]);

  useEffect(() => {
    if (selectedMapDate && !availableDates.includes(selectedMapDate)) {
      setSelectedMapDate(null);
    }
  }, [availableDates, selectedMapDate]);

  // Default to the latest day with GPS so the map does not render the heavy "all" aggregate.
  useEffect(() => {
    if (didAutoSelectDateRef.current) return;
    if (availableDates.length === 0) return;
    didAutoSelectDateRef.current = true;
    setSelectedMapDate(availableDates[availableDates.length - 1]);
  }, [availableDates]);

  useEffect(() => {
    if (!needsHeatmap) return;
    if (!selectedPeriod && !selectedMonth) {
      setHeatmapData(null);
      return;
    }
    if (heatmapFetchedKeyRef.current === periodFetchKey) return;

    let isCancelled = false;
    setHeatmapDataLoading(true);

    const timeRange = resolveGeographicTimeRange(selectedMonth, selectedPeriod);
    const periodsToFetch =
      serviceType === 'emergency_services'
        ? ['JUL24']
        : selectedMonth && Array.isArray(periods) && periods.length > 0
          ? periods.filter((p) => periodContainsMonth(p, selectedMonth))
          : selectedPeriod
            ? [selectedPeriod]
            : [];
    const unique = Array.from(new Set(periodsToFetch)).filter(Boolean);

    const fetchBatchHeatmap = async () => {
      if (unique.length === 0) return null;
      const fetchOne = async (p: string) => {
        const url = `/api/data/${serviceType}/analytics/geographic/${p}/heatmap-data`;
        const res = await fetch(url);
        if (!res.ok) {
          if (res.status === 404) throw new Error(`Heatmap JSON data not found for period "${p}".`);
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }
        return await res.json();
      };
      const payloads = await Promise.all(unique.map((p) => fetchOne(p)));
      return payloads.reduce((acc, cur) => deepMergeConcatArrays(acc, cur), null as any);
    };

    (async () => {
      try {
        // Prefer truth-layer GPS for logistics (Samsara / Optifleet / Supatrak ingest).
        if (serviceType === 'logistics' && timeRange) {
          try {
            const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.geographic.heatmap(timeRange));
            if (res.ok) {
              const payload = await res.json();
              if (hasGeographicVehicles(payload)) {
                if (!isCancelled) {
                  heatmapFetchedKeyRef.current = periodFetchKey;
                  setHeatmapData(payload);
                  setHeatmapDataError(null);
                  setHeatmapDataLoading(false);
                }
                return;
              }
            }
          } catch (truthErr) {
            console.warn('[GeographicInsights] Truth heatmap failed, trying batch fallback:', truthErr);
          }
        }

        const merged = await fetchBatchHeatmap();
        if (!isCancelled) {
          heatmapFetchedKeyRef.current = periodFetchKey;
          setHeatmapData(merged);
          setHeatmapDataError(null);
          setHeatmapDataLoading(false);
        }
      } catch (error) {
        if (!isCancelled) {
          const msg = error instanceof Error ? error.message : 'Unknown error';
          setHeatmapData(null);
          setHeatmapDataError(
            serviceType === 'logistics'
              ? `No GPS heatmap data for this period. Ingest telematics into the truth layer, or provide batch geographic JSON. (${msg})`
              : `Failed to load heatmap data: ${msg}`,
          );
          setHeatmapDataLoading(false);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [needsHeatmap, periodFetchKey, selectedPeriod, periods, selectedMonth, serviceType]);

  useEffect(() => {
    if (!needsRoute) return;
    if (!selectedPeriod && !selectedMonth) {
      setRouteData(null);
      return;
    }
    if (routeFetchedKeyRef.current === periodFetchKey) return;

    let isCancelled = false;
    setRouteDataLoading(true);

    const timeRange = resolveGeographicTimeRange(selectedMonth, selectedPeriod);
    const periodsToFetch =
      serviceType === 'emergency_services'
        ? ['JUL24']
        : selectedMonth && Array.isArray(periods) && periods.length > 0
          ? periods.filter((p) => periodContainsMonth(p, selectedMonth))
          : selectedPeriod
            ? [selectedPeriod]
            : [];
    const unique = Array.from(new Set(periodsToFetch)).filter(Boolean);

    const fetchBatchRoute = async () => {
      if (unique.length === 0) return null;
      const fetchOne = async (p: string) => {
        const url = `/api/data/${serviceType}/analytics/geographic/${p}/route-data`;
        const res = await fetch(url);
        if (!res.ok) {
          if (res.status === 404) throw new Error(`Route JSON data not found for period "${p}".`);
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }
        return await res.json();
      };
      const payloads = await Promise.all(unique.map((p) => fetchOne(p)));
      return payloads.reduce((acc, cur) => deepMergeConcatArrays(acc, cur), null as any);
    };

    (async () => {
      try {
        if (serviceType === 'logistics' && timeRange) {
          try {
            const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.geographic.route(timeRange));
            if (res.ok) {
              const payload = await res.json();
              if (hasGeographicVehicles(payload)) {
                if (!isCancelled) {
                  routeFetchedKeyRef.current = periodFetchKey;
                  setRouteData(payload);
                  setRouteDataError(null);
                  setRouteDataLoading(false);
                }
                return;
              }
            }
          } catch (truthErr) {
            console.warn('[GeographicInsights] Truth route failed, trying batch fallback:', truthErr);
          }
        }

        const merged = await fetchBatchRoute();
        if (!isCancelled) {
          routeFetchedKeyRef.current = periodFetchKey;
          setRouteData(merged);
          setRouteDataError(null);
          setRouteDataLoading(false);
        }
      } catch (error) {
        if (!isCancelled) {
          const msg = error instanceof Error ? error.message : 'Unknown error';
          setRouteData(null);
          setRouteDataError(
            serviceType === 'logistics'
              ? `No GPS route data for this period. Ingest telematics into the truth layer, or provide batch geographic JSON. (${msg})`
              : `Failed to load route data: ${msg}`,
          );
          setRouteDataLoading(false);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [needsRoute, periodFetchKey, selectedPeriod, periods, selectedMonth, serviceType]);

  const handleRetry = () => {
    heatmapFetchedKeyRef.current = null;
    routeFetchedKeyRef.current = null;
    setHeatmapData(null);
    setRouteData(null);
    setHeatmapDataError(null);
    setRouteDataError(null);
    setHeatmapDataLoading(needsHeatmap);
    setRouteDataLoading(needsRoute);
    setRetryCount((c) => c + 1);
  };

  return (
    <section className="mt-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">Geographic Insights</h2>
        <p className="text-muted-foreground text-sm">
          View service areas and routes on a map.
        </p>
      </div>
      <div className="bg-card rounded-lg shadow-sm p-6 border border-secondary">
        <div ref={mapSectionRef} className="rounded-lg overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Interactive Map</h3>
            <div className="flex items-center justify-end gap-1">
              <button
                type="button"
                onClick={() => setServiceAreaMapTab('heatmap')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all border ${
                  serviceAreaMapTab === 'heatmap'
                    ? 'bg-primary text-white border-primary'
                    : 'bg-secondary text-foreground border-border hover:bg-accent'
                }`}
              >
                Heatmap
              </button>
              <button
                type="button"
                onClick={() => setServiceAreaMapTab('coverage')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all border ${
                  serviceAreaMapTab === 'coverage'
                    ? 'bg-primary text-white border-primary'
                    : 'bg-secondary text-foreground border-border hover:bg-accent'
                }`}
              >
                Coverage Map
              </button>
              <button
                type="button"
                onClick={() => setServiceAreaMapTab('route')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all border ${
                  serviceAreaMapTab === 'route'
                    ? 'bg-primary text-white border-primary'
                    : 'bg-secondary text-foreground border-border hover:bg-accent'
                }`}
              >
                Route Map
              </button>
            </div>
          </div>
          <div
            className="overflow-hidden rounded-lg"
            style={{
              minHeight: '600px',
              height: '70vh',
            }}
          >
            <div className="w-full h-full flex flex-col">
              <div className="flex-1 min-h-0 relative">
                {!heatmapData && !routeData && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-background z-10">
                    {!mapInView ? (
                      <p className="text-muted-foreground">Scroll to load map data…</p>
                    ) : heatmapDataLoading || routeDataLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-warning mb-4" />
                        <p className="text-muted-foreground">Loading map data...</p>
                      </>
                    ) : (
                      <>
                        <p className="text-muted-foreground mb-2 text-lg font-medium">Map data not available</p>
                        {(heatmapDataError || routeDataError) && (
                          <p className="text-muted-foreground text-sm max-w-md text-center">
                            {heatmapDataError || routeDataError}
                          </p>
                        )}
                        <p className="text-accent text-xs mt-4 max-w-md text-center">
                          GPS comes from the truth layer (<code className="bg-card px-2 py-1 rounded">vehicle_position_observed</code>).
                          Ensure telematics ingest has completed for this month, then retry.
                        </p>
                        <button
                          onClick={handleRetry}
                          className="mt-4 px-4 py-2 bg-ring hover:bg-ring text-white rounded-md text-sm"
                        >
                          Retry
                        </button>
                      </>
                    )}
                  </div>
                )}
                {(heatmapData || routeData) && (
                  <MapView
                    center={[(heatmapData ?? routeData).center[1], (heatmapData ?? routeData).center[0]]}
                    zoom={(heatmapData ?? routeData).zoom}
                    theme="dark"
                    fullscreenTargetRef={mapSectionRef}
                  >
                    <MapControls position="bottom-right" showZoom showCompass showLocate showFullscreen />
                    {(heatmapData ?? routeData).station_markers?.map(
                      (
                        marker: { lat: number; lon: number; name: string; popup: string },
                        idx: number
                      ) => (
                        <MapMarker key={idx} longitude={marker.lon} latitude={marker.lat}>
                          <MarkerContent>
                            <div className="relative cursor-pointer">
                              <svg
                                width="36"
                                height="48"
                                viewBox="0 0 24 32"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="drop-shadow-lg"
                                style={{ filter: 'drop-shadow(0 0 3px rgba(255,255,255,0.8))' }}
                              >
                                <path
                                  d="M12 0C7.58 0 4 3.58 4 8C4 14 12 24 12 24C12 24 20 14 20 8C20 3.58 16.42 0 12 0Z"
                                  fill={STATUS_COLORS.warning}
                                  stroke="white"
                                  strokeWidth="1.5"
                                />
                                <rect
                                  x="7"
                                  y="10"
                                  width="10"
                                  height="8"
                                  fill="white"
                                  stroke={STATUS_COLORS.warning}
                                  strokeWidth="0.5"
                                />
                                <rect x="8" y="12" width="2" height="2" fill={STATUS_COLORS.warning} />
                                <rect x="11" y="12" width="2" height="2" fill={STATUS_COLORS.warning} />
                                <rect x="14" y="12" width="2" height="2" fill={STATUS_COLORS.warning} />
                                <line x1="7" y1="14" x2="17" y2="14" stroke={STATUS_COLORS.warning} strokeWidth="0.5" />
                                <polygon points="9,10 12,7 15,10" fill="white" />
                              </svg>
                            </div>
                          </MarkerContent>
                          <MarkerTooltip className="!p-0 !m-0 bg-background text-white rounded shadow-lg">
                            <div
                              className="px-2 py-1 text-xs"
                              dangerouslySetInnerHTML={{ __html: sanitizeMapPopupHtml(marker.popup) }}
                            />
                          </MarkerTooltip>
                        </MapMarker>
                      )
                    )}
                    {serviceAreaMapTab === 'heatmap' && heatmapData && (
                      <GeographicHeatmap
                        data={heatmapData}
                        redact={isDemoMode}
                        embedInMap
                        getVehicleDisplayName={getVehicleDisplayName}
                        selectedMonth={selectedMonth}
                        selectedDate={selectedMapDate}
                        availableDates={availableDates}
                        onSelectedDateChange={setSelectedMapDate}
                        groupBy={mapGroupBy}
                        selectedGroupValue={mapSelectedGroupValue}
                        showAll={mapShowAll}
                        selectedActivity={mapSelectedActivity}
                        onGroupByChange={setMapGroupBy}
                        onSelectedGroupValueChange={setMapSelectedGroupValue}
                        onShowAllChange={setMapShowAll}
                        onSelectedActivityChange={setMapSelectedActivity}
                      />
                    )}
                    {serviceAreaMapTab === 'coverage' && heatmapData && (
                      <GeographicCoverageMap
                        data={heatmapData}
                        redact={isDemoMode}
                        embedInMap
                        getVehicleDisplayName={getVehicleDisplayName}
                        selectedMonth={selectedMonth}
                        selectedDate={selectedMapDate}
                        availableDates={availableDates}
                        onSelectedDateChange={setSelectedMapDate}
                        groupBy={mapGroupBy}
                        selectedGroupValue={mapSelectedGroupValue}
                        showAll={mapShowAll}
                        selectedActivity={mapSelectedActivity}
                        onGroupByChange={setMapGroupBy}
                        onSelectedGroupValueChange={setMapSelectedGroupValue}
                        onShowAllChange={setMapShowAll}
                        onSelectedActivityChange={setMapSelectedActivity}
                      />
                    )}
                    {serviceAreaMapTab === 'route' && routeData && (
                      <GeographicRouteMap
                        data={routeData}
                        redact={isDemoMode}
                        embedInMap
                        getVehicleDisplayName={getVehicleDisplayName}
                        selectedMonth={selectedMonth}
                        selectedDate={selectedMapDate}
                        availableDates={availableDates}
                        onSelectedDateChange={setSelectedMapDate}
                        groupBy={mapGroupBy === 'Station' ? 'Type' : mapGroupBy}
                        selectedGroupValue={mapSelectedGroupValue}
                        showAll={mapShowAll}
                        onGroupByChange={(v) => setMapGroupBy(v)}
                        onSelectedGroupValueChange={setMapSelectedGroupValue}
                        onShowAllChange={setMapShowAll}
                      />
                    )}
                  </MapView>
                )}
                {(heatmapData || routeData) &&
                  ((serviceAreaMapTab !== 'route' && heatmapDataLoading && !heatmapData) ||
                    (serviceAreaMapTab === 'route' && routeDataLoading && !routeData)) && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/80 z-10">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-warning mb-4" />
                      <p className="text-muted-foreground">
                        {serviceAreaMapTab === 'route' ? 'Loading route data...' : 'Loading heatmap data...'}
                      </p>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
